import { AreaDetail, ClimbingArea } from "../../types";
import { AdminAreaLink } from "../common";
import { WeatherIcon } from "../weatherIcon";
import { WindArrow } from "../windArrow";
import { compassDirection, forecastDayConditions, parseNumber, rateForecastDay } from "../../utils/conditions";
import { forecastHighlights, Highlight, verdictLine } from "../../utils/areaForecast";
import { ConditionsBadge, ConditionsMeter } from "./conditionsScore";
import { FavoriteButton } from "./favoriteButton";
import { RatingButton } from "../ratingButton";
import { Landscape } from "../landscape";
import { IconInfo, IconRain, IconStar } from "./icons";
import "./areaHero.css";

interface AreaHeroProps {
    area: ClimbingArea;
    detail: AreaDetail | null;
    now: number;
}

export function AreaHero({ area, detail, now }: AreaHeroProps) {
    const forecast = area.forecast;
    const current = forecast?.hourly?.data?.[0];
    const today = forecast?.daily?.data?.[0];
    const todayRating = forecast && today ? rateForecastDay(forecast, 0) : null;
    const highlights = forecast ? forecastHighlights(forecast, now) : [];

    const temperature = parseNumber(current?.temperature) ?? parseNumber(today?.temperatureHigh);
    const wind = parseNumber(current?.windSpeed);
    const gust = parseNumber(current?.windGust);
    const bearing = parseNumber(current?.windBearing);
    const direction = bearing !== undefined && wind !== undefined && Math.round(wind) > 0 ? bearing : undefined;
    const precip = parseNumber(current?.precipProbability);
    const humidity = parseNumber(current?.humidity);

    const elevation = parseNumber(detail?.elevation);
    const meta = [
        elevation !== undefined ? `${Math.round(elevation).toLocaleString("en-US")} ft` : null,
        `${Number(area.latitude).toFixed(2)}, ${Number(area.longitude).toFixed(2)}`,
    ].filter(Boolean).join(" · ");

    return (
        <section className="area-hero" aria-labelledby="area-hero-title">
            <div className="area-hero__inner">
                <div className="area-hero__main">
                    <div className="area-hero__top">
                        <nav className="area-hero__crumbs" aria-label="Breadcrumb">
                            <AdminAreaLink code={area.adminArea} className="area-hero__crumb-link">{area.adminAreaName}</AdminAreaLink>
                            <span aria-hidden="true"> › </span>
                            <span aria-current="page">{area.name}</span>
                        </nav>
                        <FavoriteButton area={area} />
                    </div>
                    <h1 className="area-hero__title" id="area-hero-title">{area.name}</h1>
                    <p className="area-hero__meta">{meta}</p>

                    {temperature !== undefined ? (
                        <div className="area-hero__now">
                            <WeatherIcon code={current?.icon ?? today?.icon} size={72} onDark />
                            <p className="area-hero__temp">
                                <span className="cw-sr-only">Now </span>{Math.round(temperature)}°
                            </p>
                            <dl className="area-hero__stats">
                                {wind !== undefined && (
                                    <div>
                                        <dt>Wind</dt>
                                        <dd>
                                            {direction !== undefined && <WindArrow bearing={direction} size={13} />}
                                            {Math.round(wind)} mph
                                            {direction !== undefined && ` from the ${compassDirection(direction)}`}
                                            {gust !== undefined && gust > wind ? `, gusts ${Math.round(gust)}` : ""}
                                        </dd>
                                    </div>
                                )}
                                {precip !== undefined && <div><dt>Precip</dt><dd>{Math.round(precip * 100)}%</dd></div>}
                                {humidity !== undefined && <div><dt>Humidity</dt><dd>{Math.round(humidity * 100)}%</dd></div>}
                            </dl>
                        </div>
                    ) : (
                        <p className="area-hero__line">The forecast isn’t available right now.</p>
                    )}

                    {todayRating && (
                        <div className="area-hero__verdict">
                            <RatingButton conditions={forecastDayConditions(forecast!, 0)} period="day" context={`${area.name} · Today`} onDark>
                                <ConditionsMeter rating={todayRating} suffix="today" onDark />
                            </RatingButton>
                            <p className="area-hero__line">{verdictLine(todayRating)}</p>
                        </div>
                    )}
                </div>

                {highlights.length > 0 && (
                    <aside className="area-hero__highlights" aria-labelledby="area-hero-highlights">
                        <h2 className="area-hero__highlights-title" id="area-hero-highlights"><IconStar />Highlights</h2>
                        <ul className="area-hero__highlights-list">
                            {highlights.map((h) => (
                                <li key={h.kind} className="area-hero__highlight">
                                    <HighlightIcon highlight={h} />
                                    <p><strong>{h.lead}</strong> {h.text}</p>
                                </li>
                            ))}
                        </ul>
                    </aside>
                )}
            </div>
            <Landscape className="area-hero__landscape" />
        </section>
    );
}

function HighlightIcon({ highlight }: { highlight: Highlight }) {
    if (highlight.kind === "window" && highlight.score) {
        return <span className="area-hero__highlight-badge"><ConditionsBadge score={highlight.score} onDark /></span>;
    }
    if (highlight.kind === "rain") {
        return <span className="area-hero__highlight-icon area-hero__highlight-icon--rain"><IconRain size={26} /></span>;
    }
    return <span className="area-hero__highlight-icon"><IconInfo size={26} /></span>;
}
